import React, { useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Spin, Tabs } from 'antd';
import { ArrowLeftOutlined, LeftOutlined } from '@ant-design/icons';
import PageScaffold from '../../components/PageScaffold';
import CompanyContext from '../../contexts/company/CompanyContext';
import { RECEPTIONIST_AI_PAGE_PATH } from './ReceptionistAiListPage';
import useReceptionistAiDetailPageController from './controllers/useReceptionistAiDetailPageController';
import { getReceptionistAiStatusModifier } from './types/ReceptionistAiDisplay';
import {
  buildActivityChips,
  buildInstructionsChips,
  buildPickupChips,
  buildPresentationChips,
  greetingMentionsAiDisclosure,
} from './receptionistAi.helpers';
import ConciergeBellIcon from './components/ConciergeBellIcon';
import SettingsStepCard from './components/SettingsStepCard';
import DonaStatusSection from './sections/DonaStatusSection';
import TestReceptionistSection from './sections/TestReceptionistSection';
import PresentationSection from './sections/PresentationSection';
import ActivityMotifsSection from './sections/ActivityMotifsSection';
import AdditionalInstructionsSection from './sections/AdditionalInstructionsSection';
import PickupSection from './sections/PickupSection';
import GuardrailsInfoSection from './sections/GuardrailsInfoSection';
import CompanyPageHeader from '../company/CompanyPageHeader';
import './ReceptionistAiDetailPage.css';

export const RECEPTIONIST_AI_DETAIL_PAGE_PATH = `${RECEPTIONIST_AI_PAGE_PATH}/:subscriptionId`;
export const RECEPTIONIST_AI_SETTINGS_TITLE = 'Paramètres des réceptionnistes';
export const RECEPTIONIST_AI_SETTINGS_SUBTITLE = 'Personnalisez Donna pour chaque utilisateur de votre équipe.';
const RECEPTIONIST_AI_MEMBER_SETTINGS_TITLE = 'Ma réceptionniste';

interface ReceptionistAiDetailPageProps { }

const ReceptionistAiDetailPage: React.FC<ReceptionistAiDetailPageProps> = () => {
  const navigate = useNavigate();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const { activeCompany } = useContext(CompanyContext);
  const { state, actions } = useReceptionistAiDetailPageController();
  const {
    detail,
    isLoading,
    isSaving,
    userFirstName,
    headerSubtitle,
    status,
    enabled,
    assistantName,
    represents,
    transmitsTo,
    greeting,
    ringCount,
    triggers,
    activities,
    motifModes,
    instructions,
    activityGroups,
    alwaysMotifs,
    applicableMotifsCount,
    customMotifsCount,
    selectedActivitiesLabel,
  } = state;
  const {
    setEnabled,
    setAssistantName,
    setRepresents,
    setTransmitsTo,
    setGreeting,
    setRingCount,
    toggleTrigger,
    toggleActivity,
    setMotifMode,
    setInstructions,
    applyDefaultGreeting,
    handleSave,
  } = actions;

  const isSupervisor = !!activeCompany?.isSupervisor;
  const name = assistantName || 'Donna';
  const pageTitle = userFirstName ? `Donna pour ${userFirstName}` : 'Donna';
  // A member only sees their own receptionist, not the team's
  const settingsTitle = isSupervisor ? RECEPTIONIST_AI_SETTINGS_TITLE : RECEPTIONIST_AI_MEMBER_SETTINGS_TITLE;
  const settingsSubtitle = isSupervisor ? RECEPTIONIST_AI_SETTINGS_SUBTITLE : `Personnalisez ${name} pour vos appels.`;

  return (
    <PageScaffold header={<CompanyPageHeader />} title={settingsTitle} hideHeader>
      <div className="receptionist-ai-detail-page__page-head">
        <div className="page-scaffold__page-head">
          <h1>{settingsTitle}</h1>
          <h3>{settingsSubtitle}</h3>
        </div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(RECEPTIONIST_AI_PAGE_PATH)}>
          Retour aux appels
        </Button>
      </div>

      <Tabs
        className="receptionist-ai-page__tabs"
        activeKey="parametres"
        onChange={(tab) => tab === 'appels' && navigate(RECEPTIONIST_AI_PAGE_PATH)}
        items={[
          { key: 'appels', label: 'Appels' },
          { key: 'parametres', label: settingsTitle },
        ]}
      />

      <div className="receptionist-ai-detail-page__header">
        {isSupervisor && (
          <Button
            icon={<LeftOutlined />}
            className="receptionist-ai-detail-page__back"
            onClick={() => navigate(`${RECEPTIONIST_AI_PAGE_PATH}?tab=parametres`)}
            aria-label="Retour à la liste des réceptionnistes"
          />
        )}
        <span className="receptionist-ai-detail-page__header-avatar">
          <ConciergeBellIcon />
        </span>
        <div className="receptionist-ai-detail-page__header-text">
          <h1 className="receptionist-ai-detail-page__header-title">{pageTitle}</h1>
          {headerSubtitle && <p className="receptionist-ai-detail-page__header-subtitle">{headerSubtitle}</p>}
        </div>
        <div className="receptionist-ai-detail-page__header-actions">
          {status && (
            <span
              className={`receptionist-ai-detail-page__header-status receptionist-ai-detail-page__header-status--${getReceptionistAiStatusModifier(status)}`}
            >
              {status}
            </span>
          )}
          {subscriptionId && detail && (
            <TestReceptionistSection assistantName={assistantName} subscriptionId={subscriptionId} />
          )}
          <Button type="primary" onClick={handleSave} loading={isSaving} disabled={isLoading || !detail}>
            Enregistrer
          </Button>
        </div>
      </div>

      {isLoading || !detail ? (
        <div className="receptionist-ai-detail-page__loading">
          <Spin size="large" />
        </div>
      ) : (
        <div className="receptionist-ai-detail-page">
          <DonaStatusSection
            assistantName={assistantName}
            userFirstName={userFirstName}
            enabled={enabled}
            onEnabledChange={setEnabled}
            stats={detail.stats}
          />
          <SettingsStepCard
            step={1}
            title={`Comment ${name} se présente`}
            chips={buildPresentationChips(name, represents, transmitsTo)}
            warning={greetingMentionsAiDisclosure(greeting) ? undefined : 'Annonce IA manquante'}
          >
            <PresentationSection
              assistantName={assistantName}
              onAssistantNameChange={setAssistantName}
              represents={represents}
              onRepresentsChange={setRepresents}
              transmitsTo={transmitsTo}
              onTransmitsToChange={setTransmitsTo}
              greeting={greeting}
              onGreetingChange={setGreeting}
              onApplyDefaultGreeting={applyDefaultGreeting}
            />
          </SettingsStepCard>
          <SettingsStepCard
            step={2}
            title={userFirstName ? `L’activité de ${userFirstName} et ce que fait ${name}` : `L’activité et ce que fait ${name}`}
            chips={buildActivityChips(selectedActivitiesLabel, applicableMotifsCount, customMotifsCount)}
          >
            <ActivityMotifsSection
              assistantName={assistantName}
              activityGroups={activityGroups}
              alwaysMotifs={alwaysMotifs}
              selectedActivityIds={activities}
              onToggleActivity={toggleActivity}
              motifModes={motifModes}
              onSetMotifMode={setMotifMode}
            />
          </SettingsStepCard>
          <SettingsStepCard step={3} title="Consignes additionnelles" chips={buildInstructionsChips(instructions)}>
            <AdditionalInstructionsSection
              assistantName={assistantName}
              value={instructions}
              onChange={setInstructions}
            />
          </SettingsStepCard>
          <SettingsStepCard step={4} title={`Quand ${name} décroche`} chips={buildPickupChips(triggers, ringCount)}>
            <PickupSection
              assistantName={assistantName}
              userFirstName={userFirstName}
              triggers={triggers}
              onToggleTrigger={toggleTrigger}
              ringCount={ringCount}
              onRingCountChange={setRingCount}
            />
          </SettingsStepCard>
          <GuardrailsInfoSection assistantName={assistantName} />
        </div>
      )}
    </PageScaffold>
  );
};

export default ReceptionistAiDetailPage;
